import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, X } from 'lucide-react';

interface Column<T> {
  id: string;
  header: string;
  sortable?: boolean;
  cell: (value: any, row: T) => React.ReactNode;
  sticky?: 'left' | 'right';
  stickyWidth?: string;
}

interface DataGridProps<T> {
  data: T[];
  columns: Column<T>[];
  isLoading?: boolean;
  searchTerm?: string;
  onSearch?: (term: string) => void;
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  searchPlaceholder?: string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  rowTooltip?: string;
  maxHeight?: string;
  getRowClassName?: (row: T) => string;
  activeDropdownId?: string | null;
}

function DataGrid<T>({
  data,
  columns,
  isLoading = false,
  searchTerm = '',
  onSearch,
  currentPage = 1,
  pageSize = 10,
  totalCount = 0,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  onSort,
  sortField,
  sortDirection,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data available',
  onRowClick,
  rowTooltip = 'Open Job Contact Sheet',
  maxHeight = '400px',
  getRowClassName,
  activeDropdownId
}: DataGridProps<T>) {
  const pageSizeOptions = [10, 25, 50, 100];
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search - wait 800ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch && localSearchTerm !== searchTerm) {
        onSearch(localSearchTerm);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [localSearchTerm, onSearch, searchTerm]);

  // Update local search term when external searchTerm changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSort = (columnId: string) => {
    if (!onSort || !columns.find(col => col.id === columnId)?.sortable) return;
    let nextDirection: 'asc' | 'desc' = 'asc';
    if (sortField === columnId) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    onSort(columnId, nextDirection);
  };

  const getSortIcon = (columnId: string) => {
    const isActive = sortField === columnId;
    return (
      <span className="inline-flex items-center w-4 h-4 ml-1">
        {isActive && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Search Bar */}
      {onSearch && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {localSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg" style={{ height: maxHeight, overflow: 'auto', position: 'relative', isolation: 'isolate' }}>
          <table className="w-full border-separate border-spacing-0">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-[400]">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.id}
                    className={`px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${
                      column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''
                    } ${
                      (column.sticky === 'right') ? `bg-gray-50 dark:bg-gray-900 border-l border-gray-300 dark:border-gray-700` : 
                      (column.sticky === 'left') ? `bg-gray-50 dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700` : ''
                    }`}
                    style={column.sticky ? { 
                      width: column.stickyWidth || 'auto',
                      zIndex: column.id === 'actions' ? 402 : 401,
                      position: 'sticky',
                      [column.sticky === 'right' ? 'right' : 'left']: 0,
                      boxShadow: column.sticky === 'right' ? '-2px 0 4px rgba(0,0,0,0.05)' : undefined
                    } : {}}
                    onClick={() => handleSort(column.id)}
                  >
                    <div className="flex items-center">
                      <span>{column.header}</span>
                      {column.sortable && getSortIcon(column.id)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500 dark:text-gray-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={index}
                    data-row-id={(row as any).id}
                    className={`group hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${onRowClick ? 'cursor-pointer' : ''} ${getRowClassName ? getRowClassName(row) : ''}`}
                    onClick={onRowClick && !activeDropdownId ? () => onRowClick(row) : undefined}
                    title={onRowClick ? rowTooltip : undefined}
                  >
                    {columns.map((column) => (
                      <td 
                        key={column.id} 
                        className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 ${
                          column.sticky === 'right' ? `bg-white dark:bg-gray-800 border-l border-gray-300 dark:border-gray-700` : 
                          column.sticky === 'left' ? `bg-white dark:bg-gray-800 border-r border-gray-300 dark:border-gray-700` : ''
                        }`}
                        style={column.sticky ? { 
                          width: column.stickyWidth || 'auto',
                          zIndex: column.id === 'actions' ? 350 : 300,
                          position: 'sticky',
                          [column.sticky === 'right' ? 'right' : 'left']: 0,
                          boxShadow: column.sticky === 'right' ? '-2px 0 4px rgba(0,0,0,0.05)' : undefined
                        } : {}}
                      >
                        {column.cell((row as any)[column.id], row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </div>

      {/* Pagination */}
      {(onPageChange || onPageSizeChange) && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {totalCount === 0 
                  ? 'No results found'
                  : `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} results`
                }
              </span>
              {onPageSizeChange && (
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              )}
            </div>
            {onPageChange && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataGrid;
